'use client'

// Where the dots come from.
//
// They used to be lit by a live event landing while you happened to be
// watching: a JUDGE on the socket set a flag, and that was the only route.
// Which meant the ordinary case, him reading your offering an hour after
// you closed the tab, lit nothing at all. You came back to a HUD that
// looked exactly like a HUD with nothing waiting on it.
//
// So the dots are derived instead. The server says when each channel last
// moved, the store remembers the newest thing you have actually looked at,
// and a dot is one being ahead of the other. That answer is the same after
// a reload, on another device, or a week later, and no event can be missed
// because nothing is being caught.
//
// The live socket path stays where it is: when you ARE watching, the dot
// should appear immediately rather than up to a poll later.

import { useCallback, useEffect } from 'react'
import useGameStore from '../../stores/gameStore'
import { authFetch, useAccount } from '../auth/useUser'

const POLL_MS = 45_000

export default function NoticeWatcher() {
  const { signedIn, ready } = useAccount()
  const setNoticeState = useGameStore((s) => s.setNoticeState)

  const read = useCallback(() => {
    authFetch('/api/notices', { cache: 'no-store' })
      .then((r) => r.json())
      .then((b) => {
        if (!b || typeof b !== 'object') return
        setNoticeState({
          lastJudgedAt: Number(b.lastJudgedAt) || 0,
          lastBallotAt: Number(b.lastBallotAt) || 0,
          openBallots: Number(b.openBallots) || 0,
        })
      })
      .catch(() => { /* a dot is not worth a retry storm */ })
  }, [setNoticeState])

  // Re-read when the session lands as well as on a timer: signed out, the
  // offerings half of the answer is missing, and it arrives seconds later.
  useEffect(() => {
    if (!ready) return
    read()
    const id = setInterval(read, POLL_MS)
    return () => clearInterval(id)
  }, [ready, signedIn, read])

  // Also when the tab comes back. Somebody returning after two days is
  // exactly who this is for, and they should not wait 45 seconds to find
  // out something happened while they were gone.
  useEffect(() => {
    const onWake = () => { if (document.visibilityState === 'visible') read() }
    document.addEventListener('visibilitychange', onWake)
    return () => document.removeEventListener('visibilitychange', onWake)
  }, [read])

  return null
}

/**
 * The dots themselves, derived. Read this rather than the raw flags: it is
 * the comparison that makes a dot mean "since you last looked" instead of
 * "while you were watching".
 */
export function useDerivedNotices() {
  const notices = useGameStore((s) => s.notices)
  const state = useGameStore((s) => s.noticeState)
  const seen = useGameStore((s) => s.seen)

  return {
    // `you` still comes from the persisted rank comparison in the profile
    // drawer, which already worked the way everything else now does.
    you: !!notices.you,
    offerings: !!notices.offerings || state.lastJudgedAt > (seen.offerings || 0),
    calendar: !!notices.calendar || state.lastBallotAt > (seen.calendar || 0),
  }
}
